import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useEffect, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {FileResponseBody} from '@dms/api/shared';
import {
  useCancelIssueVehicleWarehouseMovementMutation,
  useGetSaleVehicleWarehouseInformationQuery,
  useLazyGetVehicleWarehouseMovementQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {getApiDateString, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface VehicleWarehouseCancelStockOutProps extends TestIdProps {
  vehicleId: string;
  saleVehicleId: string;
  onDocumentCreation: (document: FileResponseBody) => void;
  onMovementComplete?: () => void;
}

interface VehicleWarehouseCancelStockOutFormFields {
  note: string | null;
  cancellationDate: string;
}

export function VehicleWarehouseCancelStockOut(props: VehicleWarehouseCancelStockOutProps) {
  const [isDocumentSelected, setIsDocumentSelected] = useState(true);
  const [documentTemplate, setDocumentTemplate] = useState<TemplateBoxTemplate>();

  const [getTemplates, {isLoading: isTemplatesLoading, isError: isTemplatesError}] =
    useDocumentTemplates();
  const {defaultTemplates, selectedTemplates, selectedAuthProfileLabel, primaryTemplate} =
    getTemplates('cancel-issue-vehicle-warehouse-movement');

  const {data: warehouseInfo} = useGetSaleVehicleWarehouseInformationQuery({
    vehicleId: props.vehicleId,
    saleVehicleId: props.saleVehicleId,
  });

  const [cancelStockOut] = useCancelIssueVehicleWarehouseMovementMutation();
  const [getMovement] = useLazyGetVehicleWarehouseMovementQuery();

  useEffect(() => {
    if (isNotNilOrEmpty(documentTemplate)) {
      return;
    }

    setDocumentTemplate(primaryTemplate);
  }, [documentTemplate, primaryTemplate]);

  const onSubmit: FormSubmitHandler<VehicleWarehouseCancelStockOutFormFields> = (values) =>
    cancelStockOut({
      vehicleId: props.vehicleId,
      saleVehicleId: props.saleVehicleId,
      body: {
        issueCancellationTemplateId: isDocumentSelected ? documentTemplate?.id : undefined,
        note: values.note,
        cancellationDate: values.cancellationDate,
      },
    })
      .unwrap()
      .then((response) => {
        if (!isDocumentSelected) {
          showNotification.success(i18n.t('entity.vehicle.labels.vehicleStockOutCancelled'));
          closeCurrentDialog();

          return;
        }

        return response;
      })
      .then((response) => {
        if (!response) {
          return;
        }

        return getMovement({
          vehicleWarehouseMovementId: response?.id,
        })
          .unwrap()
          .then((response) => {
            response.document && props.onDocumentCreation(response.document);
          });
      })
      .then(props.onMovementComplete)
      .catch(handleApiError);

  return (
    <VStack spacing={4}>
      <Text size="small" color="secondary">
        {i18n.t('entity.vehicleWarehouse.modal.cancelStockOutDescription')}
      </Text>
      <DataStatus isLoading={isTemplatesLoading} isError={isTemplatesError}>
        <Form<VehicleWarehouseCancelStockOutFormFields>
          onSubmit={onSubmit}
          schema={schema}
          defaultValues={{
            cancellationDate: warehouseInfo?.issuedAt ?? getApiDateString(new Date()),
          }}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <Box maxWidth="50%">
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.dateOfStockIn')}
                  name="cancellationDate"
                  type="apiDate"
                  control={control}
                  isRequired
                  data-testid={suffixTestId('cancelStockOut-cancellationDate', props)}
                />
              </Box>
              <DocumentTemplateBox
                data-testid={suffixTestId('documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseCancelStockOutDocument')}
                templates={defaultTemplates}
                authorizationTemplates={selectedTemplates}
                authorizationProfileLabel={selectedAuthProfileLabel}
                selectedTemplate={documentTemplate}
                isSelected={isDocumentSelected}
                onSelect={setIsDocumentSelected}
                onTemplateChange={setDocumentTemplate}
                onNoteChange={(value) => formApi.setValue('note', value)}
                noteLabel={i18n.t(
                  'entity.document.labels.vehicleWarehouseCancelStockOutDocumentNote'
                )}
                isWithNote
                isDisabled
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancelStockOut-cancelBtn', props)}
                />
                <FormButton
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  control={control}
                  data-testid={suffixTestId('cancelStockOut-submitBtn', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}

const schema = object({
  note: yupString,
});
